import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Users, Quote, Building, Code, ArrowLeft, ArrowRight, Play, Pause, Sparkles } from 'lucide-react';

const testimonialsData = [
  {
    name: 'Wilfredo Perlacio',
    role: 'CTO, FinTech Solutions',
    company: 'TechNova',
    quote: 'La API de consulta RUC redujo nuestro tiempo de validación de 2 minutos a menos de 3 segundos. La documentación es excelente y el soporte responde en minutos.',
    avatar: 'https://images.unsplash.com/photo-1758634832507-72a43d0f8656?q=80&w=764&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    rating: 5,
    integration: 'Sistema de Facturación',
    metrics: '+300% velocidad',
    tech: ['Node.js', 'React', 'MongoDB']
  },
  {
    name: 'Carlos Ruiz',
    role: 'Líder de Desarrollo',
    company: 'ContaPlus',
    quote: 'Implementamos el envío masivo de facturas en una semana. Las APIs son robustas y los webhooks nos permiten tener tracking en tiempo real.',
    avatar: 'https://images.unsplash.com/photo-1655716063663-bd95f6129054?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    rating: 5,
    integration: 'Plataforma Contable',
    metrics: '1 semana de integración',
    tech: ['Python', 'Django', 'PostgreSQL']
  },
  {
    name: 'María González',
    role: 'Arquitecta de Software',
    company: 'ERP Solutions',
    quote: 'La estabilidad del servicio es impresionante. 99.9% uptime real con latencias consistentes menores a 100ms incluso en horario pico.',
    avatar: 'https://plus.unsplash.com/premium_photo-1738854510968-d1ad8cf53a1f?q=80&w=735&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    rating: 5,
    integration: 'Sistema ERP',
    metrics: '99.9% uptime',
    tech: ['Java', 'Spring Boot', 'MySQL']
  },
  {
    name: 'Juan Pérez',
    role: 'Fundador & Dev Lead',
    company: 'StartupX',
    quote: 'Los SDKs en múltiples lenguajes nos permitieron integrar rápidamente. El sandbox fue crucial para testing antes de producción.',
    avatar: 'https://images.unsplash.com/photo-1708786454216-d8cea2748171?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    rating: 5,
    integration: 'E-commerce Platform',
    metrics: 'Multi-language SDK',
    tech: ['JavaScript', 'PHP', 'Ruby']
  }
];

const Testimonials = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAuto, setIsAuto] = useState(true);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    if (!isAuto) return;
    const interval = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isAuto]);

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length);
    setIsAuto(false);
  };

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
    setIsAuto(false);
  };

  const toggleAutoPlay = () => {
    setIsAuto(!isAuto);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.9
    })
  };


  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden border-t border-gray-200">
      {/* Efectos de fondo mejorados */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>
        
        {/* Partículas animadas */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-500/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              scale: [1, 2, 1],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
        
        {/* Gradientes de fondo */}
        <div className="absolute top-1/4 -left-10 w-72 h-72 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-10 w-72 h-72 bg-cyan-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Encabezado mejorado */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl px-6 py-3 mb-8 backdrop-blur-sm"
          >
            <div className="relative">
              <Users className="w-5 h-5 text-blue-600" />
              <motion.div
                className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            <span className="text-sm font-semibold text-blue-700 tracking-wide">500+ INTEGRACIONES EXITOSAS</span>
            <Sparkles className="w-4 h-4 text-blue-500" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-6xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Desarrolladores{' '}
            <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-800 bg-clip-text text-transparent">
              Confían
            </span>{' '}
            en Nosotros
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light"
          >
            Empresas líderes optimizaron sus procesos SUNAT con nuestras APIs. 
            <span className="font-semibold text-blue-600"> Descubre sus experiencias.</span>
          </motion.p>
        </motion.div>

        {/* Testimonial Principal Mejorado */}
        <motion.div
          className="relative max-w-6xl mx-auto mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="bg-white/80 backdrop-blur-sm border border-blue-200/50 rounded-3xl shadow-2xl p-8 md:p-12 overflow-hidden relative">
            {/* Efecto de gradiente en borde */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/5 to-cyan-500/5 -z-10"></div>
            
            {/* Quote icon decorativo */}
            <div className="absolute top-6 right-6 opacity-5">
              <Quote className="w-32 h-32 text-blue-500" />
            </div>

            <div className="relative h-96 flex items-center justify-center">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.4 },
                    scale: { duration: 0.4 }
                  }}
                  className="absolute inset-0 flex flex-col items-center justify-center space-y-8 px-4"
                >
                  {/* Rating con efecto */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="flex justify-center space-x-1 mb-2"
                  >
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.3 + i * 0.1 }}
                      >
                        <Star
                          className={`w-7 h-7 ${i < testimonialsData[currentIndex].rating 
                            ? 'text-yellow-500 fill-current drop-shadow-sm' 
                            : 'text-gray-300'}`}
                        />
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* Quote */}
                  <motion.blockquote
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-2xl md:text-3xl text-gray-800 leading-relaxed text-center font-light max-w-4xl"
                  >
                    "{testimonialsData[currentIndex].quote}"
                  </motion.blockquote>

                  {/* Información del cliente mejorada */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="text-center space-y-4"
                  >
                    <div className="flex items-center justify-center space-x-6">
                      <motion.img
                        whileHover={{ scale: 1.1 }}
                        src={testimonialsData[currentIndex].avatar}
                        alt={testimonialsData[currentIndex].name}
                        className="w-20 h-20 rounded-2xl border-4 border-white shadow-lg"
                      />
                      <div className="text-left">
                        <h4 className="font-bold text-gray-900 text-xl">{testimonialsData[currentIndex].name}</h4>
                        <p className="text-gray-600 text-lg">{testimonialsData[currentIndex].role}</p>
                        <div className="flex items-center space-x-4 mt-2">
                          <div className="flex items-center space-x-1 text-blue-600">
                            <Building className="w-4 h-4" />
                            <span className="text-sm font-medium">{testimonialsData[currentIndex].company}</span>
                          </div>
                          <div className="flex items-center space-x-1 text-cyan-600">
                            <Code className="w-4 h-4" />
                            <span className="text-sm font-medium">{testimonialsData[currentIndex].integration}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Métricas y tecnologías */}
                    <div className="flex items-center justify-center space-x-6 pt-4">
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
                      >
                        {testimonialsData[currentIndex].metrics}
                      </motion.div>
                      <div className="flex space-x-2">
                        {testimonialsData[currentIndex].tech.map((tech, index) => (
                          <motion.span
                            key={tech}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-medium border"
                          >
                            {tech}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Controles de navegación mejorados */}
            <div className="flex items-center justify-between mt-8">
              <div className="flex items-center space-x-4">
                <motion.button
                  whileHover={{ scale: 1.1, backgroundColor: "#3b82f6" }}
                  whileTap={{ scale: 0.9 }}
                  onClick={toggleAutoPlay}
                  className="bg-blue-500 text-white p-3 rounded-2xl shadow-lg hover:shadow-xl transition-all"
                >
                  {isAuto ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </motion.button>
                
                <div className="flex space-x-2">
                  {testimonialsData.map((_, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.2 }}
                      onClick={() => {
                        setDirection(index > currentIndex ? 1 : -1);
                        setCurrentIndex(index);
                        setIsAuto(false);
                      }}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentIndex 
                          ? 'bg-blue-600 w-8 scale-125' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <motion.button
                  whileHover={{ scale: 1.1, x: -2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handlePrev}
                  className="bg-white border border-gray-300 rounded-2xl p-4 shadow-lg hover:shadow-xl hover:border-blue-400 transition-all group"
                >
                  <ArrowLeft className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1, x: 2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleNext}
                  className="bg-white border border-gray-300 rounded-2xl p-4 shadow-lg hover:shadow-xl hover:border-blue-400 transition-all group"
                >
                  <ArrowRight className="w-6 h-6 text-gray-600 group-hover:text-blue-600 transition-colors" />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Grid de testimonios secundarios mejorado */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {testimonialsData.slice(0, 3).map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="group bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            >
              {/* Efecto de fondo al hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              
              <div className="flex items-start space-x-4 mb-4">
                <motion.img
                  whileHover={{ rotate: 5 }}
                  src={testimonial.avatar}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-xl border-2 border-white shadow-md group-hover:border-blue-200 transition-colors"
                />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h5 className="font-bold text-gray-900 text-base">{testimonial.name}</h5>
                      <p className="text-gray-600 text-sm">{testimonial.role}</p>
                    </div>
                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-semibold">
                      {testimonial.metrics}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex mb-4 space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < testimonial.rating 
                      ? 'text-yellow-500 fill-current' 
                      : 'text-gray-300'}`}
                  />
                ))}
              </div>
              
              <p className="text-gray-700 text-sm leading-relaxed line-clamp-4 group-hover:line-clamp-none transition-all">
                "{testimonial.quote}"
              </p>
              
              <div className="flex flex-wrap gap-2 mt-4">
                {testimonial.tech.map((tech) => (
                  <span
                    key={tech}
                    className="bg-blue-500/10 text-blue-700 px-2 py-1 rounded-lg text-xs font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Final Mejorado */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-3xl p-12 max-w-4xl mx-auto relative overflow-hidden shadow-2xl">
            {/* Efectos de fondo */}
            <div className="absolute inset-0 bg-black/5"></div>
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-bold text-white mb-4"
              >
                ¿Listo para unirte a ellos?
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto"
              >
                Comienza tu integración hoy y optimiza tus procesos SUNAT con nuestras APIs de alta performance
              </motion.p>
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 20px 40px rgba(255,255,255,0.2)"
                }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-bold text-lg border-0 hover:shadow-2xl transition-all duration-300 inline-flex items-center space-x-3"
              >
                <Sparkles className="w-6 h-6" />
                <span>Comenzar Integración Gratis</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;